import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Tractor, Upload, Loader2, CheckCircle2, ArrowLeft, Sprout, XCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Vegetables', 'Grains', 'Tubers', 'Fruits', 'Livestock', 'Poultry', 'Fish', 'Dairy', 'Nuts', 'Herbs & Spices'];

const FarmerApply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    farm_name: '', location: '', phone: '',
    years_experience: 1, farm_size_hectares: 0.5, bio: '',
  });
  const [categories, setCategories] = useState([]);
  const [photo, setPhoto] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farmer/me`);
      if (r.data.profile) {
        setProfile(r.data.profile);
        setForm({
          farm_name: r.data.profile.farm_name || '',
          location: r.data.profile.location || '',
          phone: r.data.profile.phone || '',
          years_experience: r.data.profile.years_experience || 1,
          farm_size_hectares: r.data.profile.farm_size_hectares || 0.5,
          bio: r.data.profile.bio || '',
        });
        setCategories(r.data.profile.produce_categories || []);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/auth?redirect=/farmer/apply', { replace: true });
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleCategory = (c) => {
    setCategories((prev) => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.farm_name || !form.location || !form.phone) {
      toast.error('Farm name, location and phone are required');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append('farm_name', form.farm_name);
    fd.append('location', form.location);
    fd.append('phone', form.phone);
    fd.append('produce_categories', categories.join(','));
    fd.append('years_experience', form.years_experience);
    fd.append('farm_size_hectares', form.farm_size_hectares);
    fd.append('bio', form.bio);
    if (photo) fd.append('photo', photo);
    try {
      await axios.post(`${API}/farmer/apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Application submitted — we\'ll review within 1–2 business days.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>;

  if (profile?.status === 'approved') {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="farmer-already-approved">
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold mb-1">You&apos;re a verified farmer</h1>
            <p className="text-gray-500 mb-4">Head to your dashboard to post commodities.</p>
            <Link to="/farmer/listings"><Button className="bg-green-600 hover:bg-green-700">Manage my listings</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatusBadge = () => {
    if (!profile) return null;
    if (profile.status === 'pending') return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Under review</Badge>;
    if (profile.status === 'rejected') return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected — re-apply below</Badge>;
    return null;
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="farmer-apply-page">
      <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Tractor className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <CardTitle>Apply to become a farmer</CardTitle>
              <div className="mt-1"><StatusBadge /></div>
              {profile?.admin_notes && profile.status === 'rejected' && (
                <p className="text-xs text-red-700 mt-1">Notes: {profile.admin_notes}</p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold block mb-1">Farm name*</label>
                <Input value={form.farm_name} onChange={e => setForm(f => ({...f, farm_name: e.target.value}))} data-testid="apply-farm-name" />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">Phone*</label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="apply-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1">Farm location* (state / city)</label>
              <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="e.g. Ogun State, Abeokuta" data-testid="apply-location" />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-2">Produce categories (multi-select)</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => toggleCategory(c)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      categories.includes(c)
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
                    }`}
                    data-testid={`apply-category-${c}`}
                  >{c}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold block mb-1">Years of experience</label>
                <Input type="number" min={0} value={form.years_experience} onChange={e => setForm(f => ({...f, years_experience: e.target.value}))} />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">Farm size (hectares)</label>
                <Input type="number" min={0} step={0.1} value={form.farm_size_hectares} onChange={e => setForm(f => ({...f, farm_size_hectares: e.target.value}))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1">About your farm (optional)</label>
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]" maxLength={1000} value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1">Farm photo (optional)</label>
              <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-md p-3 cursor-pointer hover:border-green-500">
                <Upload className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-500">{photo ? photo.name : 'Click to upload (JPG/PNG/WebP)'}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setPhoto(e.target.files?.[0])} className="hidden" data-testid="apply-photo-input" />
              </label>
            </div>
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={submitting}
              data-testid="apply-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sprout className="h-4 w-4 mr-2" />}
              {profile ? 'Re-submit application' : 'Submit application'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FarmerApply;
